import {
  Box,
  HStack,
  VStack,
  Input as ChakraInput,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Icon,
  Radio,
  GridItem,
  Grid,
  Text,
  Stack,
} from '@chakra-ui/react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import CustomInput from '@/components/common/Input';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import PercentageIcon from '../../../assets/svg/percentage.svg?react';
import DollarIcon from '../../../assets/svg/dollar.svg?react';
import SelectedIcon from '../../../assets/svg/selected.svg?react';
import { getCurrencySymbol } from '@/utils/currencyUtils';

export interface ICreateCouponData {
  code: string;
  discount_percent?: string | null;
  money_value_off?: string | null;
  start_at: string;
  end_at: string;
  total_issues: number;
  coupon_limit_per_customer: number;
  min_purchase: number;
  coupon_type?: string | undefined;
}

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .max(10, 'Promotion must be less than 10 characters')
    .required('Promotion name is required.'),
  discount_percent: Yup.string()
    .nullable()
    .when('coupon_type', {
      is: 'percentage',
      then: (schema) =>
        schema
          .required('Discount percentage is required.')
          .test('discount-range', 'Discount percentage must be between 1 and 99', (value) => {
            if (!value) return false;
            const numValue = parseFloat(value);
            return numValue > 0 && numValue < 100;
          }),
      otherwise: (schema) =>
        schema
          .nullable()
          .test(
            'money-discount-positive',
            'Discount percentage must be greater than 0',
            (value) => {
              if (!value || value === '') return true;
              const numValue = parseFloat(value);
              return numValue > 0;
            },
          ),
    }),

  money_value_off: Yup.string()
    .nullable()
    .when('coupon_type', {
      is: 'money',
      then: (schema) =>
        schema
          .required('Discount amount is required.')
          .test('money-positive', 'Discount amount must be greater than 0', (value) => {
            if (!value) return false;
            const numValue = parseFloat(value);
            return numValue > 0;
          }),
      otherwise: (schema) => schema.nullable(),
    }),
  start_at: Yup.string().required('Start date is required.'),
  end_at: Yup.string().required('End date is required.'),
  total_issues: Yup.number()
    .min(1, 'Total issues must be at least 1')
    .required('Total issues are required.')
    .test(
      'total-issues-limit',
      'Total issues must be greater than or equal to coupon limit per customer',
      function (value) {
        const { coupon_limit_per_customer } = this.parent;
        if (!value || !coupon_limit_per_customer) return true;
        return value >= coupon_limit_per_customer;
      },
    ),
  coupon_limit_per_customer: Yup.number()
    .min(1, 'Coupon limit per customer must be at least 1')
    .required('Coupon limit per customer is required.')
    .test(
      'customer-limit-total',
      'Coupon limit per customer cannot exceed total issues',
      function (value) {
        const { total_issues } = this.parent;
        if (!value || !total_issues) return true;
        return value <= total_issues;
      },
    ),
  min_purchase: Yup.number()
    .required('Minimum purchase is required.')
    .min(1, 'Minimum purchase must be at least 1'),
  coupon_type: Yup.string().optional(),
});
interface CreateCouponFormProps {
  onSubmit: (data: ICreateCouponData) => void;
  isSuccess: boolean;
  currency?: string; // Currency prop
}

const CreateCouponForm = forwardRef(
  (
    { onSubmit, isSuccess, currency = 'USD' }: CreateCouponFormProps,
    ref: React.Ref<() => void>,
  ) => {
    const {
      control,
      handleSubmit,
      setValue,
      reset,
      watch,
      formState: { errors },
    } = useForm<ICreateCouponData>({
      resolver: yupResolver(validationSchema),
      defaultValues: {
        code: '',
        discount_percent: '0',
        money_value_off: '0',
        start_at: '',
        end_at: '',
        total_issues: 0,
        coupon_limit_per_customer: 0,
        min_purchase: 0,
        coupon_type: 'percentage_discount',
      },
    });

    useImperativeHandle(ref, () => handleSubmit(onSubmit));

    useEffect(() => {
      if (isSuccess) {
        reset();
      }
    }, [isSuccess, reset]);

    const [selectedRadio, setSelectedRadio] = useState<'percentage' | 'money' | null>('percentage');

    const handleRadioChange = (value: 'percentage' | 'money') => {
      setSelectedRadio(value);
      setValue('coupon_type', value);
    };

    const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '');
      e.target.value = value;
    };

    return (
      <Box as="form" fontFamily={'primary'} mb={2}>
        <HStack mt={2} mb={4}>
          <Grid
            templateColumns={{
              base: '1fr',
              sm: 'repeat(2, 1fr)',
            }}
            gap={4}
            width="100%"
          >
            <GridItem>
              <HStack
                justifyContent={'space-between'}
                alignItems={'center'}
                px={'14px'}
                py={'10px'}
                borderRadius={'xl'}
                bg={'background.white'}
                borderWidth={'1px'}
                borderColor={
                  selectedRadio === 'percentage' ? 'brand.primary.DEFAULT' : 'border.default'
                }
                onClick={() => handleRadioChange('percentage')}
                cursor="pointer"
              >
                <HStack>
                  <Icon as={PercentageIcon} boxSize={10} />
                  <Text fontSize={'md'} color={'text.primary'} fontWeight={'medium'}>
                    Percentage Discount
                  </Text>
                </HStack>
                <Radio
                  isChecked={selectedRadio === 'percentage'}
                  value="percentage"
                  borderColor="transparent"
                  colorScheme="brand"
                  size="lg"
                  borderWidth="1px"
                  alignItems="center"
                  mt={2}
                >
                  {selectedRadio === 'percentage' && (
                    <Icon as={SelectedIcon} color={'brand.primary.DEFAULT'} boxSize={5} />
                  )}
                </Radio>
              </HStack>
            </GridItem>
            <GridItem>
              <HStack
                justifyContent={'space-between'}
                px={'14px'}
                py={'10px'}
                borderRadius={'xl'}
                borderWidth={'1px'}
                bg={'background.white'}
                borderColor={selectedRadio === 'money' ? 'brand.primary.DEFAULT' : 'border.default'}
                onClick={() => handleRadioChange('money')}
                cursor="pointer"
              >
                <HStack>
                  <Icon as={DollarIcon} boxSize={10} />
                  <Text fontSize={'md'} color={'text.primary'} fontWeight={'medium'}>
                    Money Value Off
                  </Text>
                </HStack>
                <Radio
                  isChecked={selectedRadio === 'money'}
                  value="money"
                  borderColor="transparent"
                  colorScheme="brand"
                  size="lg"
                  borderWidth="1px"
                  alignItems="center"
                  mt={2}
                >
                  {selectedRadio === 'money' && (
                    <Icon as={SelectedIcon} color={'brand.primary.DEFAULT'} boxSize={5} />
                  )}
                </Radio>
              </HStack>
            </GridItem>
          </Grid>
        </HStack>

        <VStack gap={4}>
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label="Promotion Name"
                placeholder="Enter promotion name"
                error={errors.code}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  field.onChange(value);
                }}
              />
            )}
          />
          <Stack
            direction={{ base: 'column', md: 'row' }}
            width="100%"
            spacing={2}
            align="flex-start"
          >
            <Controller
              name="start_at"
              control={control}
              render={({ field }) => (
                <FormControl isInvalid={!!errors.start_at}>
                  <FormLabel
                    htmlFor="start_at"
                    color={errors.start_at ? 'status.error' : 'text.secondary'}
                    fontSize={'sm'}
                    fontFamily={'primary'}
                    fontWeight={'normal'}
                    mb={'6px'}
                  >
                    Start Date
                  </FormLabel>
                  <ChakraInput
                    {...field}
                    type="date"
                    placeholder="Start Date"
                    min={new Date().toISOString().split('T')[0]}
                    borderColor={errors.start_at ? 'status.error' : 'border.default'}
                    bg={'background.white'}
                  />
                  <FormErrorMessage fontSize={'xs'} color={'status.error'}>
                    {errors.start_at?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
            <Text
              color={'text.secondary'}
              mt={{ base: '0px', md: '35px' }}
              mx={{ base: '0px', md: '8px' }}
            >
              to
            </Text>
            <Controller
              name="end_at"
              control={control}
              render={({ field }) => (
                <FormControl isInvalid={!!errors.end_at}>
                  <FormLabel
                    htmlFor="end_at"
                    color={errors.end_at ? 'status.error' : 'text.secondary'}
                    fontSize={'sm'}
                    fontFamily={'primary'}
                    fontWeight={'normal'}
                    mb={'6px'}
                  >
                    End Date
                  </FormLabel>
                  <ChakraInput
                    {...field}
                    type="date"
                    min={watch('start_at') || new Date().toISOString().split('T')[0]}
                    disabled={!watch('start_at')}
                    placeholder="End Date"
                    borderColor={errors.end_at ? 'status.error' : 'border.default'}
                    bg={'background.white'}
                  />
                  <FormErrorMessage fontSize={'xs'} color={'status.error'}>
                    {errors.end_at?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </Stack>
          {selectedRadio === 'percentage' && (
            <Controller
              name="discount_percent"
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  label="Discount Percentage"
                  placeholder="Enter discount percent"
                  error={errors.discount_percent}
                  rightIcon={<span>%</span>}
                  value={field.value ?? '0'}
                  onInput={handleNumericInput}
                />
              )}
            />
          )}
          {selectedRadio === 'money' && (
            <Controller
              name="money_value_off"
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  label="Discount Price"
                  placeholder="Enter discount price"
                  error={errors.money_value_off}
                  leftIcon={<span>{getCurrencySymbol(currency)}</span>} // Use currency prop
                  value={field.value ?? '0'}
                  onInput={handleNumericInput}
                />
              )}
            />
          )}
          <Controller
            name="min_purchase"
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label="Order Min. Spend"
                placeholder="Enter price"
                error={errors.min_purchase}
                leftIcon={<span>{getCurrencySymbol(currency)}</span>} // Use currency prop
                value={field.value || ''}
                onInput={handleNumericInput}
              />
            )}
          />
          <Controller
            name="total_issues"
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label="Total Coupon Issues"
                placeholder="Enter number of coupons"
                error={errors.total_issues}
                value={field.value || ''}
                onInput={handleNumericInput}
              />
            )}
          />
          <Controller
            name="coupon_limit_per_customer"
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label="Coupon Limit Per Customer"
                placeholder="Enter limit"
                error={errors.coupon_limit_per_customer}
                value={field.value || ''}
                onInput={handleNumericInput}
              />
            )}
          />
        </VStack>
      </Box>
    );
  },
);

export default CreateCouponForm;
